import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useThemeUI } from "theme-ui";
import flow from "lodash/fp/flow";

import { selectUserChoiceValues, selectUserChoices } from "store/app";
import { Button } from "components";
import { useTranslation } from "hooks";
import { ButtonOptions, FieldsSelector } from "components/Forms";
import { ChevronLeftIcon } from "components/icons";
import { FORM_COMPONENTS } from "utils/constants";

export default function GroupedButtonOptions({
  label: rawLabel,
  options,
  toolTip,
  value,
  isFieldTouched,
  setTouchedField,
  onChange,
  setFocusedField,
  goBackFocusedFields,
  defaultValueToSetOnRender,
}) {
  const { t } = useTranslation();

  const {
    theme: { isMobile },
  } = useThemeUI();

  const userChoiceValues = useSelector(selectUserChoiceValues);
  const userChoices = useSelector(selectUserChoices);

  const [displayOptionsFor, setDisplayOptionsFor] = useState("");

  const goBack = () => {
    setDisplayOptionsFor("");
    goBackFocusedFields();
  };

  useEffect(() => {
    if (displayOptionsFor) {
      setFocusedField();
    }
  }, [displayOptionsFor]);

  // Parent is always shown as selected when value is selected and fieldIsTouched for GroupedButtonOptionsComponent.
  const parentShouldBeSelected = value?.selectedOption?.label && isFieldTouched;

  const label = [!parentShouldBeSelected && t("SELECT"), rawLabel]
    .filter(Boolean)
    .join(" ");

  // hideLabelWhenValueIsSelected: is defined in the json for a particular groupedButtonOption Field.
  // e.g labels for 'pads', 'glove', or 'blocker' are hidden in `Construction` field when value is selected
  const hideLabelWhenValueIsSelected =
    parentShouldBeSelected && value?.hideLabelWhenValueIsSelected;

  // Can render a checkbox instead of a button if specified in json as component: "Checkbox"
  const checkboxOptions = options.filter(
    (option) => option.component === FORM_COMPONENTS.CHECKBOX
  );

  return (
    <>
      {displayOptionsFor === "" && (
        <ButtonOptions
          options={[
            {
              label: [
                !hideLabelWhenValueIsSelected && label,
                parentShouldBeSelected && value?.selectedOption?.label,
              ]
                .filter(Boolean)
                .join(": "),
              value: rawLabel,
              toolTip: toolTip,
            },
          ]}
          onChange={flow(setDisplayOptionsFor, setTouchedField)}
          isSelected={parentShouldBeSelected}
        />
      )}

      {displayOptionsFor !== "" && (
        <>
          <Button
            variant="secondary"
            size={isMobile ? "medium" : "large"}
            iconBefore={<ChevronLeftIcon />}
            aria-label="go back"
            isRound
            onClick={goBack}
          />

          <ButtonOptions
            options={options.filter((option) => !option.component)}
            value={value?.value}
            disableUnselect
            onChange={onChange}
            setTouchedField={setTouchedField}
            defaultValueToSetOnRender={defaultValueToSetOnRender}
            goBack={goBack}
          />

          {checkboxOptions.map(
            ({
              name,
              component,
              label,
              checkedValue,
              uncheckedValue,
              isDisabled,
            }) => {
              return (
                <FieldsSelector
                  key={name}
                  component={component}
                  name={name}
                  label={label}
                  userChoices={userChoices}
                  userChoiceValues={userChoiceValues}
                  checkedValue={checkedValue}
                  uncheckedValue={uncheckedValue}
                  isDisabled={isDisabled}
                  props={{
                    // for checkbox
                    applyDisabledDefaultValue: false,
                  }}
                />
              );
            }
          )}
        </>
      )}
    </>
  );
}
